// Copyright 2020 Drivetrain Hub LLC
// Plugin to add closable tabs for creating or editing objects like gears and tools.
// https://vuejs.org/v2/guide/plugins.html

// Cannot type hint PluginObject without losing variable resolution.
// eslint-disable-next-line no-unused-vars
import {PluginFunction} from "vue";
import icons from "./icons";
import requests from "../requests";
import handlers from "../handlers";
import { store } from "../store";

// lazy-loading components
const ModelGearMacro = () => import ('../components/_modelers/ModelGearMacro');
const ModelGearInternal = () => import ('../components/_modelers/ModelGearInternal');
const ModelGearMicro = () => import ('../components/_modelers/ModelGearMicro');
const ModelGearProps = () => import ('../components/_modelers/ModelGearProps');
const ModelCarrier = () => import ('../components/_modelers/ModelCarrier');
const ModelBasicRack = () => import ('../components/_modelers/ModelBasicRack');
const ModelHob = () => import ('../components/_modelers/ModelHob');
const ModelShaper = () => import ('../components/_modelers/ModelShaper');
const ReviewMesh = () => import ('../components/_reports/ReviewMesh');
const ReportModel = () => import ('../components/_reports/ReportModel');
const ReportAnalysis = () => import ('../components/_reports/ReportAnalysis');
const ReviewDrawing = () => import ('../components/_reports/ReviewDrawing');
const ReportMfg = () => import ('../components/_reports/ReportMfg');

function addViewerTab(tabPayload) {
  // display loading spinner
  store.commit('LOADING_VIEWER', true);

  // without timeout ~50ms, loading spinner does not display
  // use ~100ms to reduce "re-display glitch" of dialogs when editing lib object
  setTimeout(() => {
    store.dispatch('appendViewerTab', tabPayload);
  }, tabPayload.openId || tabPayload.lib ? 100 : 50);
}

// region PARTS

function addGearExternalTab(lib = true, openId = undefined, title = 'Gear') {
  // Add an external gear tab to viewer.  Defaults to empty form fields to create a new gear.

  const newTab = {
    title: title,
    icon: icons.gearExternal,
    content: ModelGearMacro,
    openId: openId,
    lib: lib,
  };
  addViewerTab(newTab)
}

function addGearInternalTab(lib = true, openId = undefined, title = 'Gear') {
  // Add an internal gear tab to viewer.  Defaults to empty form fields to create a new gear.

  const newTab = {
    title: title,
    icon: icons.gearInternal,
    content: ModelGearInternal,
    openId: openId,
    lib: lib,
  };
  addViewerTab(newTab)
}

// eslint-disable-next-line no-unused-vars
function addShaftTab(openId = undefined, title = 'Shaft') {
  // DO NOTHING.
  // DEPRECATED Add a shaft tab to viewer.  Defaults to empty form fields to create a new shaft frame.

  // DEPRECATED
  // const newTab = {
  //   title: title,
  //   icon: icons.partShaft,
  //   content: ModelShaft,
  //   openId: openId,
  // };
  // addViewerTab(newTab)
}

function addCarrierTab(openId = undefined, title = 'Carrier') {
  // Add a carrier tab to viewer.  Defaults to empty form fields to create a new carrier frame.

  const newTab = {
    title: title,
    icon: icons.partCarrier,
    content: ModelCarrier,
    openId: openId,
  };
  addViewerTab(newTab)
}

function addFrameTab(part) {
  // Helper function to open a shaft or carrier tab for editing based on part type.

  if (handlers.model.isPartFrameShaft(part)) {
    addShaftTab(part.Id, part.obj.name);
  } else {
    addCarrierTab(part.Id, part.obj.name);
  }
}

function addPartTab(part, lib) {
  switch (part.__typename) {
    case requests.model.TYPENAME.PART.GEAR_DETAIL: {
      const {Id, name, geom} = part.obj;

      if (handlers.gear.isInternal(geom)) {
        addGearInternalTab(lib, Id, name)
      } else {
        addGearExternalTab(lib, Id, name)
      }
      break;
    }
    case requests.model.TYPENAME.PART.FRAME:
      addFrameTab(part);
      break;
    default:
      throw Error('Unrecognized part modeler type.')
  }
}

function addGearPropsTab(openId, title = 'Properties') {
  // Add a gear properties tab to viewer.  Requires specifying the Id of part being edited.

  const emptyTab = {
    title: title,
    icon: icons.propsAtoms,
    content: ModelGearProps,
    openId: openId,
  };
  addViewerTab(emptyTab)
}

function addGearMicroTab(openId, title = 'Microgeometry') {
  // Add a gear microgeometry tab to viewer.  Requires specifying the Id of part being edited.

  const emptyTab = {
    title: title,
    icon: icons.propsMicro,
    content: ModelGearMicro,
    openId: openId,
  };
  addViewerTab(emptyTab)
}

// endregion

// region TOOLS

function addBasicRackTab(openId = undefined, title = 'Basic Rack') {
  // Add a basic rack tab to viewer.  Defaults to empty form fields to create a new basic rack.

  const newTab = {
    title: title,
    icon: icons.toolBasicRack,
    content: ModelBasicRack,
    openId: openId,
  };
  addViewerTab(newTab)
}

function addHobTab(openId = undefined, title = 'Hob') {
  // Add a hob tab to viewer.  Defaults to empty form fields to create a new hob.

  const newTab = {
    title: title,
    icon: icons.toolHob,
    content: ModelHob,
    openId: openId,
  };
  addViewerTab(newTab)
}

function addShaperTab(openId = undefined, title = 'Shaper') {
  // Add a shaper tab to viewer.  Defaults to empty form fields to create a new shaper.

  const newTab = {
    title: title,
    icon: icons.toolShaper,
    content: ModelShaper,
    openId: openId,
  };
  addViewerTab(newTab)
}

// endregion

// region REVIEW

function addReviewMeshTab(openId = undefined) {
  // Add tab to viewer to review gear mesh.

  const emptyTab = {
    title: 'Mesh',
    icon: icons.connectRatio,
    content: ReviewMesh,
    openId: openId
  };
  addViewerTab(emptyTab)
}

function addReportModelTab() {
  // Add a model report tab to viewer.

  const emptyTab = {
    title: 'Model',
    icon: icons.reportGears,
    content: ReportModel,
  };
  addViewerTab(emptyTab)
}

function addReportAnalysisTab() {
  // Add an analysis report tab to viewer.

  const emptyTab = {
    title: 'Analysis',
    icon: icons.reportGears,
    content: ReportAnalysis,
  };
  addViewerTab(emptyTab)
}

function addReviewDrawingTab(openId = undefined) {
  // Add tab to viewer to review gear drawing.

  const emptyTab = {
    title: 'Drawing',
    icon: icons.setSquare,
    content: ReviewDrawing,
    openId: openId
  };
  addViewerTab(emptyTab)
}

function addReportMfgTab() {
  // Add a build/mfg report tab to viewer.

  const emptyTab = {
    title: 'Manufacturing',
    icon: icons.reportGears,
    content: ReportMfg,
  };
  addViewerTab(emptyTab)
}

// endregion

// region ANALYSIS

// endregion

/** @alias $tabs */
const Tabs = {
  // PARTS
  gear: addGearExternalTab,
  gearInternal: addGearInternalTab,
  carrier: addCarrierTab,
  part: addPartTab,
  // PROPERTIES
  gearProps: addGearPropsTab,
  gearMicro: addGearMicroTab,
  // TOOLS
  basicRack: addBasicRackTab,
  hob: addHobTab,
  shaper: addShaperTab,
  // REVIEW
  reviewMesh: addReviewMeshTab,
  reportModel: addReportModelTab,
  reportAnalysis: addReportAnalysisTab,
  reviewDrawing: addReviewDrawingTab,
  reportMfg: addReportMfgTab,
};

/** @type {PluginFunction} */
Tabs.install = function(Vue) {
  Object.defineProperty(Vue.prototype, "$tabs", {
    get () { return Tabs }
  })
};

export default Tabs
